import React, { useState, useEffect, useRef, useCallback } from "react";
import "./ImageSlider.css";
import { ArrowBack, ArrowForward } from "../../assets/exports/FileExports";

const ImageSlider = ({ slideImages }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);
  const timeoutRef = useRef(null); // for auto-advance timeout
  const navigationDotsRef = useRef(null);
  const sliderRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Preload the first image to improve LCP
  const preloadImages = useCallback(() => {
    const img = new Image();
    img.src = slideImages[0].src; // Preload the first image
  }, [slideImages]);

  // Resettable auto-advance logic
  const resetAutoSlide = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      goToNext();
    }, 7000); // 7-second delay
  }, []);

  // Trigger fade-in after first mount
  useEffect(() => {
    preloadImages(); // Preload the first image
    const timer = setTimeout(() => {
      setHasMounted(true);
    }, 50);
    return () => clearTimeout(timer);
  }, [preloadImages]);

  // // Reset the timer whenever currentIndex changes
  useEffect(() => {
    resetAutoSlide();
    return () => clearTimeout(timeoutRef.current);
  }, [currentIndex, resetAutoSlide]);

  // useEffect(() => {
  //   if (!navigationDotsRef.current || !isVisible) return; // Only scroll if visible

  //   requestAnimationFrame(() => {
  //     const dots = navigationDotsRef.current.querySelectorAll(".dot");
  //     const activeDot = dots[currentIndex];
  //     if (activeDot) {
  //       activeDot.scrollIntoView({
  //         behavior: "smooth",
  //         inline: "center",
  //         block: "nearest",
  //       });
  //     }
  //   });
  // }, [currentIndex, isVisible]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2, // Slider is considered visible when 20% is in view
      },
    );

    if (sliderRef.current) {
      observer.observe(sliderRef.current);
    }

    return () => {
      if (sliderRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        goToNext();
      } else if (e.key === "ArrowLeft") {
        goToPrevious();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVisible]);

  // Navigation handlers
  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? slideImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === slideImages.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const fetchPriorityType = (src) => {
    return <img fetchpriority="auto" src={src} alt="Slide" />;
  };

  return (
    <section ref={sliderRef} className="slider-container">
      <ul className="slider-list">
        {slideImages.map((image, index) => {
          const isActive = index === currentIndex;
          return (
            <li
              key={image.src + index}
              className={`slide ${isActive && hasMounted ? "active" : ""}`}
              tabIndex={-1}
            >
              <div className="slide-inner">{fetchPriorityType(image.src)}</div>
            </li>
          );
        })}
      </ul>

      <div
        className={`slider-heading-container ${slideImages[0].heading && "heading-available"}`}
      >
        <div className="heading-stack">
          {slideImages.map((image, index) => (
            <h3
              key={image.src + index}
              className={`slider-heading ${index === currentIndex && hasMounted ? "active" : ""}`}
              tabIndex={-1}
            >
              {image.heading}
            </h3>
          ))}
        </div>

        <div className={`navigation-panel navigation-panel-shadow`}>
          <button
            className="prev-button"
            aria-label="Previous Image"
            onClick={goToPrevious}
          >
            <span className="btn-content">
              <ArrowBack height={18} width={18} />
            </span>
          </button>

          <div className="navigation-dots-wrapper">
            <div className="navigation-dots" ref={navigationDotsRef}>
              {slideImages.map((slide, index) => (
                <button
                  aria-label={slide.heading}
                  key={index}
                  className={`dot ${index === currentIndex ? "active" : ""}`}
                  onClick={() => goToSlide(index)}
                  tabIndex={-1} // This ensures the dot doesn't steal focus
                >
                  <div className="visual-dot"></div>
                </button>
              ))}
            </div>
          </div>

          <button
            className="next-button"
            aria-label="Next Image"
            onClick={goToNext}
          >
            <span className="btn-content">
              <ArrowForward height={18} width={18} />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default React.memo(ImageSlider); // Using React.memo to avoid unnecessary re-renders
